/**
 * Tiny gettext-style translation layer.
 *
 * The English text itself is the key: `t('Fight!')`, `t('Level {n}!', { n })`.
 * Missing translations fall back to English, so wrapping a string can never
 * break the UI. Thai dictionaries live in ./th/*.ts (one file per area of the
 * game) and are merged below.
 */
import { TH } from './th';

export type Language = 'en' | 'th';
export type LanguageSetting = 'auto' | Language;

export const LANGUAGES: { id: LanguageSetting; label: string }[] = [
  { id: 'auto', label: 'Auto' },
  { id: 'en', label: 'English' },
  { id: 'th', label: 'ไทย' },
];

const DICTS: Record<Language, Record<string, string> | null> = { en: null, th: TH };

let current: Language = 'en';

/** 'auto' follows the device language (Thai when it starts with "th"). */
export function resolveLanguage(setting: LanguageSetting | undefined, navLang = typeof navigator !== 'undefined' ? navigator.language : 'en'): Language {
  if (setting === 'en' || setting === 'th') return setting;
  return navLang.toLowerCase().startsWith('th') ? 'th' : 'en';
}

export function setLanguage(lang: Language): void {
  current = lang;
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
}

export function language(): Language {
  return current;
}

/** Translate `text` and fill `{name}` placeholders. */
export function t(text: string, vars?: Record<string, string | number>): string {
  const out = DICTS[current]?.[text] ?? text;
  return vars ? out.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : out;
}

/** Plural helper: English picks `one`/`other` by n; Thai has no plural forms. */
export function tn(n: number, one: string, other: string, vars: Record<string, string | number> = {}): string {
  return t(n === 1 ? one : other, { n, ...vars });
}
